import { ApprovalsService } from 'api-clients/monolith';
import { useCallback } from 'react';

import { useApiService } from 'hooks/useApiService';

import { ApprovalRule, SubjectType } from './types';

interface RulesResult {
  rules?: Array<ApprovalRule>;
}

/**
 * The rule list for one side of the Offers | Openings toggle.
 *
 * Filtering by name happens on the server rather than over an already-fetched
 * array: the endpoint escapes LIKE wildcards, so a rule literally named
 * "50% offer" is searchable, which a naive client-side `includes` would also
 * manage — but a client-side filter silently becomes wrong the moment the list
 * is paginated, and rules are per-account configuration that a large customer
 * can accumulate a lot of. Keeping the filter where the data is means that
 * change never has to be made twice.
 *
 * `searchTerm` is expected to be debounced by the caller — this fires a request
 * per distinct value it is given.
 *
 * `enabled` is how the caller says this user may not read rules at all. The
 * endpoint authorizes `AccountPolicy#manage_approval_rules?`, and the approvals
 * route admits two further permissions that do not carry it — so without this
 * the page fires a guaranteed 403 on mount for one of the audiences it exists
 * for. The result stays `loading` while it is false, which nothing renders:
 * the same answer removes the tab that would.
 */
export const useApprovalRules = (
  subjectType: SubjectType,
  searchTerm: string,
  enabled = true,
) => {
  const service = useCallback(
    () =>
      ApprovalsService.getInternalApiApprovalsRules(
        subjectType,
        // An empty search must omit the parameter rather than send `name=`:
        // the server only applies the filter when the param is present, and
        // sending it empty would rely on that `present?` check rather than
        // saying plainly that there is no filter.
        searchTerm.trim() || undefined,
      ),
    [subjectType, searchTerm],
  );

  return useApiService<RulesResult>(service, { enabled });
};
